import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { DesignIssuesComponent } from '../../../../components/settings/voucher-design-settings/components/design-issues/design-issues.component';
import { lucideArrowLeft, lucideCircleCheck, lucideEye, lucidePencil } from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { DesignThumbnailComponent } from '../../../../components/settings/voucher-design-settings/components/design-thumbnail/design-thumbnail.component';
import {
  canActivateDesign,
  VoucherDesign,
  voucherTypeLabel,
} from '../../../../services/voucher-design.service';

/**
 * On a phone the full editor is cramped. This shows what matters there: the design,
 * a PDF preview, activation, and the way to the full editor for those who insist.
 */
@Component({
  selector: 'app-mobile-summary',
  standalone: true,
  imports: [
    DesignIssuesComponent,
    RouterModule,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmSpinnerImports,
    HlmTypographyImports,
    DesignThumbnailComponent,
  ],
  providers: [provideIcons({ lucideArrowLeft, lucideCircleCheck, lucideEye, lucidePencil })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="space-y-4 px-4 py-4">
      <a
        hlmBtn
        variant="ghost"
        size="sm"
        routerLink="/settings"
        [queryParams]="{ tab: 'voucher-design' }">
        <ng-icon name="lucideArrowLeft" />
        Einstellungen
      </a>
      <section hlmCard>
        <div hlmCardHeader>
          <h2 hlmCardTitle>{{ design().name }}</h2>
          <p hlmCardDescription class="flex flex-wrap gap-1">
            <span hlmBadge variant="secondary">{{ typeLabel }}</span>
            @if (design().is_active) {
              <span hlmBadge>In Verwendung</span>
            } @else {
              <app-design-issues [issues]="design().activation_issues" />
            }
          </p>
        </div>
        <div hlmCardContent class="space-y-3">
          <app-design-thumbnail [design]="design()" [alt]="'Vorschau ' + design().name" />
          <div class="flex flex-wrap gap-2">
            <button
              hlmBtn
              variant="outline"
              size="sm"
              type="button"
              [disabled]="previewing()"
              (click)="preview.emit()">
              @if (previewing()) {
                <hlm-spinner />
              } @else {
                <ng-icon name="lucideEye" />
              }
              Vorschau (PDF)
            </button>
            @if (!design().is_active) {
              <button
                hlmBtn
                size="sm"
                type="button"
                [disabled]="!activatable(design())"
                (click)="activate.emit()">
                <ng-icon name="lucideCircleCheck" />
                Verwenden
              </button>
            }
          </div>
          <p hlmMuted class="text-sm">
            Die Elemente platzierst du am besten an einem größeren Bildschirm. Der volle Editor
            funktioniert auch hier, ist aber eng.
          </p>
          <button hlmBtn variant="ghost" size="sm" type="button" (click)="edit.emit()">
            <ng-icon name="lucidePencil" />
            Trotzdem hier bearbeiten
          </button>
        </div>
      </section>
    </div>
  `,
})
export class MobileSummaryComponent {
  readonly activatable = canActivateDesign;
  readonly design = input.required<VoucherDesign>();
  readonly previewing = input(false);
  readonly preview = output<void>();
  readonly activate = output<void>();
  readonly edit = output<void>();

  get typeLabel(): string {
    return voucherTypeLabel(this.design().voucher_type);
  }
}
